import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/cn'

export const buttonStyles = cva(
  [
    'inline-flex items-center justify-center gap-1.5 rounded-md font-text font-medium',
    'transition-[color,background-color,border-color,transform] duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-50',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  ],
  {
    variants: {
      variant: {
        // Disabled primary goes neutral: Ember at half opacity reads as a muddy brown on graphite.
        primary:
          'bg-accent font-semibold text-accent-ink hover:bg-accent-hover active:scale-[0.98] disabled:bg-[color-mix(in_oklab,var(--ink)_10%,transparent)] disabled:text-ink-3 disabled:opacity-100 disabled:active:scale-100',
        secondary: 'border border-line-strong bg-surface text-ink hover:bg-surface-2',
        ghost: 'text-ink-2 hover:bg-surface-2 hover:text-ink',
      },
      size: {
        sm: 'h-8 px-2.5 text-small pointer-coarse:h-9',
        md: 'h-9 px-3.5 text-body',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonStyles> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonStyles({ variant, size }), className)}
      {...props}
    />
  )
})
